import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useMotionValue,
  useMotionValueEvent,
  useInView,
  animate,
  AnimatePresence,
} from "framer-motion";
import {
  ArrowRight,
  ArrowDown,
  Plus,
  Briefcase,
  PenTool,
  Workflow,
  TrendingUp,
  Code2,
  HeartHandshake,
} from "lucide-react";
import { useSEO } from "../components/SEO";
import { Button } from "../components/Button";
import { Reveal, MaskedLines, ChapterHeading } from "../components/AnimatedSection";
import { HeroVisual } from "../components/HeroVisual";
import { SystemDiagram } from "../components/SystemDiagram";
import { ServiceCard } from "../components/ServiceCard";
import { ProjectCard } from "../components/ProjectCard";
import { TestimonialCard } from "../components/TestimonialCard";
import { FAQ } from "../components/FAQ";
import { Marquee } from "../components/Marquee";
import { VideoShowcase, ShowreelFrame } from "../components/VideoShowcase";
import { services } from "../data/services";
import { projects } from "../data/projects";
import { testimonials } from "../data/testimonials";
import { faqs } from "../data/faqs";
import { industries } from "../data/industries";

const EASE = [0.22, 1, 0.36, 1];

/* ---------------- Hero ---------------- */

const Hero = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const px = useSpring(useTransform(mx, [-0.5, 0.5], [-16, 16]), { stiffness: 60, damping: 20 });
  const py = useSpring(useTransform(my, [-0.5, 0.5], [-12, 12]), { stiffness: 60, damping: 20 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      onMouseMove={onMove}
      className="bg-deep relative overflow-hidden"
      aria-label="Introduction"
    >
      <div className="bg-grid-dark absolute inset-0 opacity-60" />
      <div className="animate-float-slow pointer-events-none absolute -left-32 top-24 h-80 w-80 rounded-full bg-[#3CBE6E]/15 blur-[120px]" />
      <div className="animate-float-slow pointer-events-none absolute -right-24 bottom-10 h-96 w-96 rounded-full bg-[#B8912A]/12 blur-[130px]" style={{ animationDelay: "-7s" }} />

      {/* The connected-system animation stays alive as the hero backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, delay: 1.1 }}
        className="pointer-events-none absolute inset-0 hidden items-center justify-center md:flex"
        aria-hidden="true"
      >
        <HeroVisual px={px} py={py} className="max-w-[900px] opacity-[0.45]" />
      </motion.div>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#07130C]/85 via-[#07130C]/30 to-transparent" aria-hidden="true" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-6 pb-24 pt-40 md:px-12 lg:grid-cols-[1.1fr_0.9fr] lg:pb-32 lg:pt-48">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.55, ease: EASE }}
            className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.35em] text-white/50"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-[#3CBE6E] to-[#D3B74D]" />
            Digital Systems for Premium Businesses
          </motion.div>

          <h1 className="mt-7 font-display text-[42px] font-semibold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-[68px]">
            <MaskedLines
              trigger="mount"
              delay={1.65}
              lines={[
                "We Build the",
                <span key="l2">
                  <span className="text-gradient-aurora">Systems</span> Your
                </span>,
                "Business Runs On.",
              ]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 2.25, ease: EASE }}
            className="mt-7 max-w-xl text-base leading-relaxed text-white/60 md:text-lg"
          >
            Custom websites, SaaS platforms, CRM systems and AI automation —
            built as one connected system for your business.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 2.45, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Button to="/contact" data-testid="hero-book-call">
              Book My Free Discovery Call <ArrowRight className="h-4 w-4" />
            </Button>
            <Button variant="ghost" to="/services" data-testid="hero-explore-services">
              Explore Our Services
            </Button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 2.0, ease: EASE }}
          className="relative mx-auto w-full max-w-[260px] sm:max-w-[300px] lg:max-w-[320px]"
        >
          <ShowreelFrame eager idPrefix="hero-video" />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3, duration: 1 }}
        className="relative mx-auto flex max-w-7xl items-center gap-3 px-6 pb-10 text-[11px] uppercase tracking-[0.3em] text-white/35 md:px-12"
      >
        <ArrowDown className="h-3.5 w-3.5 animate-bounce" /> Scroll — see how it connects
      </motion.div>
    </section>
  );
};

/* ---------------- Problem: disconnected → connected ---------------- */

const TOOL_CHIPS = ["Website", "Spreadsheet", "CRM", "WhatsApp", "Email", "Reports", "Manual Tasks", "Different Tools"];
const SPOTS = [
  { left: "4%", top: "10%" }, { left: "58%", top: "2%" }, { left: "76%", top: "36%" },
  { left: "58%", top: "70%" }, { left: "10%", top: "74%" }, { left: "-2%", top: "42%" },
  { left: "28%", top: "0%" }, { left: "32%", top: "80%" },
];
const SCATTER = [
  { x: -110, y: -90, r: -14 }, { x: 70, y: -150, r: 10 }, { x: 170, y: -60, r: 16 },
  { x: 190, y: 90, r: -10 }, { x: 50, y: 160, r: 12 }, { x: -170, y: 110, r: -16 },
  { x: -50, y: -180, r: 8 }, { x: 30, y: 200, r: -12 },
];

const Problem = () => (
  <section className="bg-grid-light relative overflow-hidden bg-[#F8FBF6] py-28 md:py-36">
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <ChapterHeading
        no="01"
        kicker="The Problem"
        titleLines={[
          "Your Business Has Tools.",
          <span key="p2">
            But Do They <span className="text-gradient-signature">Work Together?</span>
          </span>,
        ]}
        sub="A website here. A spreadsheet there. Leads in WhatsApp, reports in someone's head. Every tool works — the system doesn't."
      />

      <motion.div
        initial="scattered"
        whileInView="connected"
        viewport={{ once: true, margin: "-25%" }}
        className="relative mx-auto mt-16 h-[380px] max-w-[620px] md:h-[420px]"
        data-testid="problem-transformation"
      >
        <motion.div
          variants={{
            scattered: { opacity: 0, scale: 0.6 },
            connected: { opacity: 1, scale: 1, transition: { delay: 1.35, duration: 0.7, ease: EASE } },
          }}
          className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2"
        >
          <div className="flex h-40 w-40 flex-col items-center justify-center rounded-full bg-gradient-to-br from-[#1C7543] to-[#B8912A] text-center shadow-[0_20px_60px_rgba(28,117,67,0.45)] md:h-48 md:w-48">
            <span className="px-4 font-display text-sm font-bold leading-snug text-white md:text-base">
              ONE CONNECTED SYSTEM
            </span>
          </div>
        </motion.div>
        <motion.div
          variants={{
            scattered: { opacity: 0 },
            connected: { opacity: 1, transition: { delay: 1.5, duration: 0.8 } },
          }}
          className="absolute left-1/2 top-1/2 h-56 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-[#1C7543]/40 md:h-64 md:w-64"
        />
        {TOOL_CHIPS.map((chip, i) => (
          <motion.span
            key={chip}
            variants={{
              scattered: { x: SCATTER[i].x, y: SCATTER[i].y, rotate: SCATTER[i].r, opacity: 0.45 },
              connected: { x: 0, y: 0, rotate: 0, opacity: 1, transition: { delay: 0.45, duration: 1.1, ease: EASE } },
            }}
            className="absolute rounded-full border border-[#07130C]/12 bg-white px-4 py-2 text-xs font-semibold text-[#07130C]/75 shadow-sm"
            style={SPOTS[i]}
          >
            {chip}
          </motion.span>
        ))}
        <motion.span
          variants={{ scattered: { opacity: 1 }, connected: { opacity: 0, transition: { delay: 0.4 } } }}
          className="absolute -top-8 left-0 text-[11px] font-bold uppercase tracking-[0.3em] text-[#B8912A]"
        >
          Disconnected tools
        </motion.span>
        <motion.span
          variants={{ scattered: { opacity: 0 }, connected: { opacity: 1, transition: { delay: 1.6 } } }}
          className="absolute -bottom-8 right-0 text-[11px] font-bold uppercase tracking-[0.3em] text-[#1C7543]"
        >
          Connected system
        </motion.span>
      </motion.div>
    </div>
  </section>
);

/* ---------------- Services overview ---------------- */

const ServicesOverview = () => (
  <section className="bg-white py-28 md:py-36">
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <ChapterHeading
        no="02"
        kicker="What We Build"
        titleLines={[
          "Five Capabilities.",
          <span key="s2" className="text-gradient-aurora">One Connected System.</span>,
        ]}
        sub="From your first line of code to the dashboard that reports on all of it — built and maintained by one team."
      />
      <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-6">
        {services.map((s, i) => (
          <div key={s.id} className={i < 2 ? "lg:col-span-3" : "lg:col-span-2"}>
            <ServiceCard service={s} i={i} />
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Connected system signature + stats ---------------- */

// EDIT: update these figures as the studio grows.
const STATS = [
  { value: "40", suffix: "+", label: "Systems Built & Deployed" },
  { value: "3,500", suffix: "+", label: "Hours of Manual Work Automated Monthly" },
  { value: "25", suffix: "+", label: "Premium Clients Served" },
  { value: "60", suffix: "%", label: "Average Reduction in Manual Reporting Time" },
];

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const numeric = parseFloat(value.replace(/,/g, ""));
  const [display, setDisplay] = useState("0");
  useEffect(() => {
    if (!inView) return undefined;
    const controls = animate(0, numeric, {
      duration: 1.8,
      ease: EASE,
      onUpdate: (v) => setDisplay(Math.round(v).toLocaleString("en-IN")),
    });
    return () => controls.stop();
  }, [inView, numeric]);
  return (
    <p ref={ref} className="font-display text-5xl font-semibold tracking-tight md:text-6xl">
      <span className="text-gradient-aurora">{display}</span>
      <span className="text-gradient-pulse">{suffix}</span>
    </p>
  );
};

const ConnectedSystem = () => (
  <section className="bg-deep relative overflow-hidden py-28 md:py-36" aria-label="Connected system">
    <div className="bg-grid-dark absolute inset-0 opacity-40" />
    <div className="animate-float-slow pointer-events-none absolute right-0 top-0 h-96 w-96 rounded-full bg-[#1C7543]/20 blur-[140px]" />
    <div className="relative mx-auto max-w-7xl px-6 md:px-12">
      <ChapterHeading
        dark
        no="03"
        kicker="The Difference"
        titleLines={[
          "Every Piece,",
          <span key="c2" className="text-gradient-pulse">Built to Work Together.</span>,
        ]}
        sub="We can build one component — a website, a CRM, an automation — or connect all of them into a single operating system for your business."
      />
      <SystemDiagram />
      <Reveal delay={0.1}>
        <p className="mx-auto mt-4 max-w-xl text-center text-sm text-white/45">
          Website → CRM → Automation → Database → Dashboard → Reports. One team.
          One architecture. No duct tape.
        </p>
      </Reveal>
    </div>

    <div className="relative mt-24 border-t border-white/10">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-y-12 px-6 py-16 md:px-12 lg:grid-cols-4">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.1}>
            <div data-testid={`stat-${i}`} className="text-center lg:text-left">
              <Counter value={s.value} suffix={s.suffix} />
              <p className="mt-3 max-w-[220px] text-sm leading-snug text-white/55 max-lg:mx-auto">
                {s.label}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Featured projects ---------------- */

const FeaturedProjects = () => (
  <section className="bg-[#F8FBF6] py-28 md:py-36">
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <div className="flex flex-wrap items-end justify-between gap-8">
        <ChapterHeading no="04" kicker="Proof" titleLines={["Real Systems We've Built."]} />
        <Reveal delay={0.2}>
          <Button variant="ghostLight" to="/project" data-testid="view-portfolio-btn">
            View Full Project Portfolio <ArrowRight className="h-4 w-4" />
          </Button>
        </Reveal>
      </div>
    </div>
    <Reveal delay={0.15}>
      <div
        data-testid="featured-projects-carousel"
        className="no-scrollbar mt-14 flex snap-x snap-mandatory gap-8 overflow-x-auto px-6 pb-4 md:px-12 lg:px-[max(3rem,calc((100vw-80rem)/2+3rem))]"
      >
        {projects.map((p) => (
          <ProjectCard key={p.slug} project={p} className="w-[82%] shrink-0 snap-start sm:w-[420px]" />
        ))}
      </div>
    </Reveal>
  </section>
);

/* ---------------- Process timeline ---------------- */

const STEPS = [
  { no: "01", title: "Discover", desc: "Understand the business and the actual problem — before any technology talk." },
  { no: "02", title: "Plan", desc: "Requirements, architecture and technology choices, mapped to your workflow." },
  { no: "03", title: "Design", desc: "Interface, experience and system design — reviewed with you, not at you." },
  { no: "04", title: "Build", desc: "Development, integrations and automation by one accountable team." },
  { no: "05", title: "Launch & Improve", desc: "Deploy, monitor, iterate. The system keeps getting better after day one." },
];

const Process = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.72", "end 0.5"] });
  const lineScale = useSpring(scrollYProgress, { stiffness: 90, damping: 26 });
  const [active, setActive] = useState(-1);
  useMotionValueEvent(scrollYProgress, "change", (v) =>
    setActive(Math.min(STEPS.length - 1, Math.floor(v * (STEPS.length + 0.4)))),
  );

  return (
    <section ref={ref} className="bg-white py-28 md:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <ChapterHeading
          no="05"
          kicker="The Process"
          titleLines={[
            "From Business Problem",
            <span key="pr2" className="text-gradient-signature">to Working System.</span>,
          ]}
        />
        <div className="relative mt-20">
          <div className="absolute left-[22px] top-0 h-full w-px bg-[#07130C]/10 lg:left-0 lg:top-[26px] lg:h-px lg:w-full" />
          <motion.div
            style={{ scaleY: lineScale, scaleX: lineScale }}
            className="absolute left-[22px] top-0 h-full w-px origin-top bg-gradient-to-b from-[#3CBE6E] via-[#1C7543] to-[#B8912A] lg:left-0 lg:top-[26px] lg:h-[3px] lg:w-full lg:origin-left lg:rounded-full"
          />
          <div className="grid gap-12 lg:grid-cols-5 lg:gap-6">
            {STEPS.map((step, i) => (
              <Reveal key={step.no} delay={i * 0.08}>
                <div data-testid={`process-step-${i}`} className="relative flex gap-6 lg:flex-col lg:gap-0">
                  <div
                    className={`z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border font-display text-sm font-bold transition-all duration-500 ${
                      i <= active
                        ? "border-transparent bg-gradient-to-br from-[#1C7543] to-[#D3B74D] text-white shadow-[0_8px_24px_rgba(28,117,67,0.4)]"
                        : "border-[#07130C]/15 bg-white text-[#07130C]/40"
                    }`}
                  >
                    {step.no}
                  </div>
                  <div className="lg:mt-6">
                    <h3 className="font-display text-lg font-semibold uppercase tracking-wide text-[#07130C]">
                      {step.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-[#07130C]/55">{step.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- Why us ---------------- */

const WHY = [
  { icon: Briefcase, title: "Business First", desc: "Every technical decision starts from your business requirement, not a template." },
  { icon: PenTool, title: "Custom Built", desc: "No systems forced into your workflow — we build around it." },
  { icon: Workflow, title: "Connected by Design", desc: "Your website, CRM and automation are built to talk to each other from day one." },
  { icon: TrendingUp, title: "Built to Scale", desc: "Architecture designed so the system grows with your business, not against it." },
];

const WhyUs = () => (
  <section className="bg-[#F8FBF6] py-28 md:py-36">
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <ChapterHeading no="06" kicker="Why Us" titleLines={["Why Us, Not Another Dev Shop?"]} />
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {WHY.map((w, i) => (
          <Reveal key={w.title} delay={i * 0.1}>
            <div
              data-testid={`why-card-${i}`}
              className="group h-full rounded-3xl border border-[#07130C]/10 bg-white p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-[#1C7543]/30 hover:shadow-[0_24px_60px_rgba(14,58,40,0.14)]"
            >
              <span className="relative flex h-14 w-14 items-center justify-center">
                <motion.svg
                  viewBox="0 0 56 56"
                  className="absolute inset-0"
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true }}
                >
                  <motion.circle
                    cx="28"
                    cy="28"
                    r="26"
                    fill="none"
                    stroke="url(#whyGrad)"
                    strokeWidth="1.6"
                    variants={{ hidden: { pathLength: 0 }, show: { pathLength: 1 } }}
                    transition={{ duration: 1.1, delay: 0.2 + i * 0.12, ease: "easeInOut" }}
                  />
                  <defs>
                    <linearGradient id="whyGrad" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#3CBE6E" />
                      <stop offset="100%" stopColor="#B8912A" />
                    </linearGradient>
                  </defs>
                </motion.svg>
                <w.icon className="h-5 w-5 text-[#1C7543] transition-transform duration-300 group-hover:scale-110" />
              </span>
              <h3 className="mt-6 font-display text-lg font-semibold uppercase tracking-wide text-[#07130C]">
                {w.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-[#07130C]/55">{w.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- About us ---------------- */

const ABOUT_FACTS = [
  { label: "Founded", value: "2022 — born from watching good businesses run on duct-taped tools" },
  { label: "Base", value: "India · Remote-first, working with clients worldwide" },
  { label: "Disciplines", value: "Engineering, design, AI automation and SEO under one roof" },
  { label: "Model", value: "Build → Launch → Monitor → Improve — a long-term technology partner" },
];

const BELIEFS = [
  {
    icon: Workflow,
    title: "Systems, Not Just Software",
    desc: "A deliverable ends at handover. A system keeps working every day. We build the second kind.",
  },
  {
    icon: Code2,
    title: "Simple Code, Premium Craft",
    desc: "Premium is how a product feels and performs — not how complicated its codebase is. We protect both.",
  },
  {
    icon: HeartHandshake,
    title: "We Stay After Launch",
    desc: "Monitoring, improving, automating and scaling — launch day is the starting line, not the finish.",
  },
];

const About = () => {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section id="about" data-testid="about-section" className="scroll-mt-24 bg-white py-28 md:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <ChapterHeading
          no="07"
          kicker="Who We Are"
          titleLines={[
            "The Team Behind",
            <span key="ab2" className="text-gradient-signature">the Systems.</span>,
          ]}
          sub="Social Wavez Technologies exists because too many good businesses run on disconnected tools and manual work."
        />

        <div className="mt-16 grid gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <Reveal>
              <p className="font-display text-xl font-medium leading-relaxed text-[#07130C] md:text-2xl">
                We're a digital systems studio of engineers, designers and
                automation specialists. We don't sell websites or software as
                isolated deliverables —{" "}
                <span className="text-gradient-aurora">
                  we design the connected system your business runs on
                </span>
                , build it, and stay with you after launch.
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-6 text-base leading-relaxed text-[#07130C]/60">
                Every engagement starts with your business problem, not a tech
                stack. That's why clinics, CA firms, coaches, real-estate teams
                and founders trust us with the systems at the center of their
                operations — and why most of them keep building with us.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-9 rounded-3xl bg-gradient-to-br from-[#3CBE6E] via-[#1C7543] to-[#D3B74D] p-[1.5px]">
                <div className="rounded-3xl bg-[#07130C] p-7 md:p-8">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#D3B74D]">
                    Our Mission
                  </p>
                  <p className="mt-3 font-display text-lg font-medium leading-relaxed text-white md:text-xl">
                    Replace disconnected tools and repetitive manual work with
                    one connected system — for every business we work with.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          <div>
            <dl>
              {ABOUT_FACTS.map((f, i) => (
                <Reveal key={f.label} delay={i * 0.08}>
                  <div data-testid={`about-fact-${i}`} className="py-5">
                    <div className="flex flex-col gap-1.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-8">
                      <dt className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.3em] text-[#07130C]/40">
                        {f.label}
                      </dt>
                      <dd className="text-right text-sm font-semibold leading-relaxed text-[#07130C] sm:max-w-[380px] md:text-base">
                        {f.value}
                      </dd>
                    </div>
                    <motion.div
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.9, delay: 0.2 + i * 0.1, ease: EASE }}
                      className="mt-5 h-px origin-left bg-gradient-to-r from-[#3CBE6E] via-[#1C7543]/30 to-transparent"
                    />
                  </div>
                </Reveal>
              ))}
            </dl>
            <Reveal delay={0.2}>
              <div ref={imgRef} className="mt-8 overflow-hidden rounded-3xl">
                <motion.div style={{ y: imgY }} className="relative">
                  <img
                    src="https://images.pexels.com/photos/23496662/pexels-photo-23496662.jpeg?auto=compress&cs=tinysrgb&w=1200"
                    alt="The Social Wavez team sketching a connected system on a whiteboard"
                    loading="lazy"
                    className="aspect-[16/9] w-full scale-110 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#07130C]/55 via-transparent to-transparent" />
                  <p className="absolute bottom-4 left-5 text-xs font-semibold uppercase tracking-[0.25em] text-white/85">
                    Systems thinking — whiteboard first, code second
                  </p>
                </motion.div>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="mt-16 grid gap-6 md:grid-cols-3">
          {BELIEFS.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.1}>
              <div
                data-testid={`about-belief-${i}`}
                className="group h-full rounded-3xl border border-[#07130C]/10 bg-[#F8FBF6] p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-[#1C7543]/30 hover:shadow-[0_24px_60px_rgba(14,58,40,0.14)]"
              >
                <span className="relative flex h-14 w-14 items-center justify-center">
                  <motion.svg
                    viewBox="0 0 56 56"
                    className="absolute inset-0"
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true }}
                  >
                    <motion.circle
                      cx="28"
                      cy="28"
                      r="26"
                      fill="none"
                      stroke="url(#aboutGrad)"
                      strokeWidth="1.6"
                      variants={{ hidden: { pathLength: 0 }, show: { pathLength: 1 } }}
                      transition={{ duration: 1.1, delay: 0.2 + i * 0.12, ease: "easeInOut" }}
                    />
                    <defs>
                      <linearGradient id="aboutGrad" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#3CBE6E" />
                        <stop offset="100%" stopColor="#D3B74D" />
                      </linearGradient>
                    </defs>
                  </motion.svg>
                  <b.icon className="h-5 w-5 text-[#1C7543] transition-transform duration-300 group-hover:scale-110" />
                </span>
                <h3 className="mt-6 font-display text-lg font-semibold text-[#07130C]">{b.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#07130C]/55">{b.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Industries ---------------- */

const Industries = () => {
  const [selected, setSelected] = useState(industries[0].id);
  const current = industries.find((i) => i.id === selected);
  return (
    <section className="bg-white py-28 md:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <ChapterHeading
          no="08"
          kicker="Who It's For"
          titleLines={[
            "Built Around How",
            <span key="i2" className="text-gradient-aurora">Your Industry</span>,
            "Actually Works.",
          ]}
        />
        <div className="mt-12 flex flex-wrap gap-3" role="tablist" aria-label="Industries">
          {industries.map((ind) => (
            <button
              key={ind.id}
              type="button"
              role="tab"
              aria-selected={selected === ind.id}
              data-testid={`industry-chip-${ind.id}`}
              onClick={() => setSelected(ind.id)}
              className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                selected === ind.id
                  ? "border-transparent bg-gradient-to-r from-[#1C7543] to-[#D3B74D] text-white shadow-[0_8px_24px_rgba(28,117,67,0.35)]"
                  : "border-[#07130C]/15 bg-white text-[#07130C]/65 hover:border-[#1C7543]/40 hover:text-[#07130C]"
              }`}
            >
              <ind.icon className="h-4 w-4" />
              {ind.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: EASE }}
            data-testid="industry-system-panel"
            className="mt-10 rounded-3xl border border-[#07130C]/10 bg-gradient-to-br from-[#F8FBF6] to-[#EAF2E2] p-8 md:p-12"
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#1C7543]">
              A typical system for {current.label}
            </p>
            <div className="mt-6 flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
              {current.system.map((piece, i) => (
                <div key={piece} className="flex items-center gap-3">
                  {i > 0 && <Plus className="hidden h-4 w-4 text-[#D3B74D] md:block" />}
                  {i > 0 && <ArrowDown className="h-4 w-4 text-[#D3B74D] md:hidden" />}
                  <span className="rounded-full border border-[#1C7543]/25 bg-white px-5 py-2.5 font-display text-sm font-semibold text-[#07130C] shadow-sm">
                    {piece}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-[#07130C]/60 md:text-base">
              {current.note}
            </p>
            <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-[#07130C]/35">
              Scoped precisely to your business on a free discovery call
            </p>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

/* ---------------- Testimonials ---------------- */

const Testimonials = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = testimonials.length;

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % n), 6000);
    return () => clearInterval(t);
  }, [paused, n]);

  const visible = [0, 1, 2].map((k) => testimonials[(index + k) % n]);

  return (
    <section
      className="bg-[#F8FBF6] py-28 md:py-36"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <ChapterHeading no="09" kicker="Trust" titleLines={["What Clients Say About Working With Us."]} />

        <div className="mt-14 hidden md:block">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="grid gap-6 md:grid-cols-3"
            >
              {visible.map((t, i) => (
                <TestimonialCard key={`${index}-${i}`} t={t} active={i === 0} />
              ))}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-14 md:hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -60 }}
              transition={{ duration: 0.4, ease: EASE }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              onDragEnd={(e, info) => {
                if (info.offset.x < -60) setIndex((i) => (i + 1) % n);
                if (info.offset.x > 60) setIndex((i) => (i - 1 + n) % n);
              }}
            >
              <TestimonialCard t={testimonials[index]} />
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-8 flex justify-center gap-2">
          {testimonials.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to testimonial ${i + 1}`}
              data-testid={`testimonial-dot-${i}`}
              onClick={() => setIndex(i)}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === index ? "w-8 bg-gradient-to-r from-[#1C7543] to-[#D3B74D]" : "w-2 bg-[#07130C]/15"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Final CTA ---------------- */

const FinalCTA = () => (
  <section className="bg-deep relative overflow-hidden py-32 md:py-44">
    <div className="bg-grid-dark absolute inset-0 opacity-50" />
    <motion.div
      animate={{ y: [0, -30, 0], x: [0, 20, 0] }}
      transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
      className="pointer-events-none absolute left-[10%] top-[15%] h-72 w-72 rounded-full bg-[#3CBE6E]/15 blur-[110px]"
    />
    <motion.div
      animate={{ y: [0, 26, 0], x: [0, -24, 0] }}
      transition={{ duration: 19, repeat: Infinity, ease: "easeInOut" }}
      className="pointer-events-none absolute bottom-[10%] right-[8%] h-80 w-80 rounded-full bg-[#B8912A]/15 blur-[120px]"
    />
    <div className="relative mx-auto max-w-4xl px-6 text-center md:px-12">
      <ChapterHeading
        dark
        no="11"
        kicker="Let's Talk"
        className="[&>div:first-child>div]:justify-center"
        titleLines={[
          "Have a Business Problem",
          <span key="f2" className="text-gradient-pulse">Technology Can Solve?</span>,
        ]}
        sub="Tell us what you're trying to build, automate, or connect — we'll map the right system on a free 20-minute call."
      />
      <Reveal delay={0.3}>
        <div className="mt-12">
          <Button to="/contact" data-testid="final-cta-book-call" className="px-10 py-5 text-base">
            Book My Free Discovery Call <ArrowRight className="h-5 w-5" />
          </Button>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- Page ---------------- */

const Home = () => {
  useSEO(
    "Social Wavez Technologies | Websites, SaaS, CRM & AI Automation",
    "Custom websites, SaaS platforms, CRM systems and AI automation built as one connected system for doctors, CAs, founders and coaches. Book a free discovery call.",
  );

  return (
    <>
      <Hero />
      <Marquee
        items={[
          "Websites", "SaaS Platforms", "CRM Systems", "AI Automation",
          "SEO Systems", "Connected by Design",
        ]}
      />
      <Problem />
      <ServicesOverview />
      <VideoShowcase />
      <ConnectedSystem />
      <FeaturedProjects />
      <Process />
      <WhyUs />
      <About />
      <Industries />
      <Testimonials />
      <section className="bg-white py-28 md:py-36">
        <div className="mx-auto max-w-3xl px-6 md:px-12">
          <ChapterHeading
            no="10"
            kicker="Questions"
            titleLines={["Asked by Every Smart Buyer."]}
          />
          <Reveal delay={0.15}>
            <div className="mt-12">
              <FAQ items={faqs} />
            </div>
          </Reveal>
        </div>
      </section>
      <FinalCTA />
    </>
  );
};

export default Home;
